/**
 * PR score-box styling — encodes **modeled profitability** on the 0–99 scale.
 * PR 50 = break-even (neutral). Below 50 shifts red; above 50 shifts green (99 ≈ max).
 *
 * Tail-risk helpers (`prTailRisk*`) remain for Market Signals copy/tooltips only.
 */

export type ProfitRatingBoxAppearance = {
  borderColor: string;
  scoreColor: string;
  backgroundImage: string;
  /** 0 at break-even, 1 at PR 100 (gain-side intensity for box styling). */
  tailRisk: number;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function parseHex(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '');
  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16),
  ];
}

function lerpHex(from: string, to: string, t: number): string {
  const [r1, g1, b1] = parseHex(from);
  const [r2, g2, b2] = parseHex(to);
  const r = Math.round(lerp(r1, r2, t));
  const g = Math.round(lerp(g1, g2, t));
  const b = Math.round(lerp(b1, b2, t));
  return `#${r.toString(16).padStart(2, '0')}${g
    .toString(16)
    .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/** Loss-side blend 1 at PR 0, 0 at PR 50+. */
export function prLossIntensity(rating: number): number {
  return rating < 50 ? (50 - rating) / 50 : 0;
}

/** Gain-side blend 0 at PR 50-, 1 at PR 100. */
export function prGainIntensity(rating: number): number {
  return rating > 50 ? (rating - 50) / 50 : 0;
}

/** 0 at PR 0, 0.5 at break-even (50), 1 at PR 100. */
export function prCopycatTailRisk(rating: number): number {
  return clamp(rating / 100, 0, 1);
}

/** @deprecated alias */
export function profitRatingIntensity(rating: number): number {
  return prCopycatTailRisk(rating);
}

export type PrTailRiskLevel = 'low' | 'moderate' | 'high';

export function prTailRiskLevel(rating: number): PrTailRiskLevel {
  if (rating >= 65) return 'high';
  if (rating >= 40) return 'moderate';
  return 'low';
}

export function prTailRiskCaption(rating: number): string {
  const level = prTailRiskLevel(rating);
  if (level === 'high') {
    return 'Profitable expert — big wins draw heavy copycat action';
  }
  if (level === 'moderate') {
    return 'Near break-even — followers may expect to win money tailing them';
  }
  return 'Historically unprofitable — less copycat concern on big wins';
}

export function prTailRiskBadgeLabel(rating: number): string | null {
  const level = prTailRiskLevel(rating);
  if (level === 'high') return 'High tail risk';
  if (level === 'moderate' && rating >= 45 && rating <= 55) return 'Break-even';
  return null;
}

export function profitRatingScoreBoxAppearance(
  rating: number,
  isDark: boolean,
): ProfitRatingBoxAppearance {
  const lossT = prLossIntensity(rating);
  const gainT = prGainIntensity(rating);

  if (isDark) {
    const neutralBorder = '#4a5568';
    const neutralScore = '#e2e8f0';
    const neutralBg1 = '#2d3748';
    const neutralBg2 = '#1a202c';
    const lossBorder = '#fc8181';
    const lossScore = '#fed7d7';
    const lossBg1 = '#4a1f24';
    const lossBg2 = '#3d1518';
    const gainBorder = '#4ade80';
    const gainScore = '#c6f6d5';
    const gainBg1 = '#1a3d2e';
    const gainBg2 = '#14532d';

    const borderColor =
      gainT > 0
        ? lerpHex(neutralBorder, gainBorder, gainT)
        : lerpHex(neutralBorder, lossBorder, lossT);
    const scoreColor =
      gainT > 0
        ? lerpHex(neutralScore, gainScore, gainT)
        : lerpHex(neutralScore, lossScore, lossT * 0.55);
    const bg1 =
      gainT > 0
        ? lerpHex(neutralBg1, gainBg1, gainT * 0.9)
        : lerpHex(neutralBg1, lossBg1, lossT * 0.85);
    const bg2 =
      gainT > 0
        ? lerpHex(neutralBg2, gainBg2, gainT * 0.9)
        : lerpHex(neutralBg2, lossBg2, lossT * 0.85);

    return {
      borderColor,
      scoreColor,
      backgroundImage: `linear-gradient(135deg, ${bg1} 0%, ${bg2} 100%)`,
      tailRisk: gainT,
    };
  }

  const neutralBorder = '#cbd5e0';
  const neutralScore = '#2d3748';
  const neutralBg1 = '#f7fafc';
  const neutralBg2 = '#edf2f7';
  const lossBorder = '#e53e3e';
  const lossScore = '#822727';
  const lossBg1 = '#fff5f5';
  const lossBg2 = '#fed7d7';
  const gainBorder = '#22c55e';
  const gainScore = '#166534';
  const gainBg1 = '#ecfdf5';
  const gainBg2 = '#bbf7d0';

  const borderColor =
    gainT > 0
      ? lerpHex(neutralBorder, gainBorder, gainT)
      : lerpHex(neutralBorder, lossBorder, lossT);
  const scoreColor =
    gainT > 0
      ? lerpHex(neutralScore, gainScore, gainT * 0.85)
      : lerpHex(neutralScore, lossScore, lossT * 0.45);
  const bg1 =
    gainT > 0
      ? lerpHex(neutralBg1, gainBg1, gainT * 0.95)
      : lerpHex(neutralBg1, lossBg1, lossT * 0.9);
  const bg2 =
    gainT > 0
      ? lerpHex(neutralBg2, gainBg2, gainT * 0.85)
      : lerpHex(neutralBg2, lossBg2, lossT * 0.75);

  return {
    borderColor,
    scoreColor,
    backgroundImage: `linear-gradient(135deg, ${bg1} 0%, ${bg2} 100%)`,
    tailRisk: gainT,
  };
}
